import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MailIcon, LockIcon, Loader2Icon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useBranding } from "@/contexts/BrandingContext";
import { getApiEndpoint } from "@/lib/api";
import { getToken, getUser, setAuth } from "@/lib/auth-storage";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";

interface CompanyInfo {
  id: number;
  name: string;
  domain: string;
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
}

const Landing = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const { branding } = useBranding();
  const { theme } = useTheme();

  // Buscar empresa do usuário logado
  useEffect(() => {
    const userData = getUser();
    const token = getToken();
    
    if (userData && token) {
      try {
        const user = JSON.parse(userData);
        if (user.company_id) {
          fetch(getApiEndpoint(`/api/companies/${user.company_id}`), {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
            .then((res) => res.ok ? res.json() : null)
            .then((data) => {
              if (data && data.company) {
                setCompanyInfo({
                  id: data.company.id,
                  name: data.company.name,
                  domain: data.company.domain,
                  logo_url: data.company.logo_url,
                  primary_color: data.company.primary_color,
                  secondary_color: data.company.secondary_color,
                });
              }
            })
            .catch((error) => {
              console.error("Erro ao buscar empresa do usuário:", error);
            });
        }
      } catch (error) {
        console.error("Erro ao parsear usuário:", error);
      }
    }
  }, []);

  // Extrair domínio da URL ou do email digitado
  useEffect(() => {
    const loadCompanyByDomain = async (domain: string) => {
      if (!domain) return;
      
      setIsLoadingCompany(true);
      try {
        const response = await fetch(getApiEndpoint(`/api/companies/domain/${domain}`));
        if (response.ok) {
          const data = await response.json();
          if (data && data.company) {
            setCompanyInfo(data.company);
          }
        }
      } catch (error) {
        console.error("Erro ao buscar empresa:", error);
      } finally {
        setIsLoadingCompany(false);
      }
    };

    // Tentar obter domínio da query string primeiro
    const domainFromQuery = searchParams.get("domain");
    if (domainFromQuery) {
      loadCompanyByDomain(domainFromQuery);
    }
  }, [searchParams]);

  // Detectar domínio quando o usuário digita o email (com debounce)
  useEffect(() => {
    const extractDomain = (email: string): string | null => {
      if (!email || !email.includes("@")) return null;
      const parts = email.split("@");
      return parts.length === 2 ? parts[1].toLowerCase().trim() : null;
    };

    const domain = extractDomain(email);
    
    // Debounce: aguardar 500ms antes de fazer a requisição
    const timeoutId = setTimeout(() => {
      if (domain && domain.length > 3) {
        setIsLoadingCompany(true);
        fetch(getApiEndpoint(`/api/companies/domain/${domain}`))
          .then((res) => {
            if (res.ok) {
              return res.json();
            }
            return null;
          })
          .then((data) => {
            if (data && data.company) {
              setCompanyInfo(data.company);
            } else {
              setCompanyInfo(null);
            }
          })
          .catch((error) => {
            console.error("Erro ao buscar empresa:", error);
            setCompanyInfo(null);
          })
          .finally(() => {
            setIsLoadingCompany(false);
          });
      } else if (!searchParams.get("domain")) {
        setCompanyInfo(null);
        setIsLoadingCompany(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [email, searchParams]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(getApiEndpoint("/api/auth/login"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        setAuth(data.token, data.user, false);

        window.dispatchEvent(new Event("userChanged"));

        toast({
          title: t.landing.loginSuccess,
          description: t.landing.redirecting,
        });

        if (data.user.role === "internal" || data.user.role === "analyst" || data.user.role === "admin") {
          navigate("/atendimento");
        } else {
          navigate("/chamados");
        }
      } else {
        toast({
          title: t.landing.loginError,
          description: data.error || t.landing.invalidCredentials,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: t.landing.loginError,
        description: t.landing.connectionError,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const displayCompany = branding
    ? {
        name: branding.name,
        logo_url: branding.logo_url,
        primary_color: branding.primary_color,
        secondary_color: branding.secondary_color,
      }
    : companyInfo;

  const logoSrc = displayCompany?.logo_url
    ? (displayCompany.logo_url.startsWith("http")
        ? displayCompany.logo_url
        : getApiEndpoint(displayCompany.logo_url))
    : null;

  // Cores personalizadas baseadas na empresa
  const primaryColor = displayCompany?.primary_color || "#2563eb";
  const secondaryColor = displayCompany?.secondary_color || "#1e40af";
  const bgGradient = displayCompany 
    ? `linear-gradient(to bottom right, ${primaryColor}15, ${secondaryColor}15)`
    : "linear-gradient(to bottom right, rgb(239 246 255), rgb(224 231 255))";

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 dark:bg-gray-900"
      style={theme === "dark" ? undefined : { background: bgGradient }}
    >
      <div className="w-full max-w-md">
        <div className="mb-8">
          <div className="text-center">
            {isLoadingCompany ? (
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <Loader2Icon className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : logoSrc ? (
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <img 
                  src={logoSrc} 
                  alt={displayCompany?.name}
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div 
                  className="w-20 h-20 rounded-full flex items-center justify-center hidden"
                  style={{ backgroundColor: primaryColor }}
                >
                  <span className="text-white font-bold text-xl">
                    {displayCompany?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
            ) : displayCompany ? (
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: primaryColor }}
              >
                <span className="text-white font-bold text-2xl">
                  {displayCompany.name.charAt(0).toUpperCase()}
                </span>
              </div>
            ) : (
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-2xl">T</span>
              </div>
            )}
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {displayCompany ? `${displayCompany.name} - ITSM` : "TEC-IT ITSM"}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {displayCompany ? `${t.landing.accessAccount} ${displayCompany.name}` : t.landing.accessAccount}
            </p>
          </div>
        </div>

        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle>{t.auth.loginTitle}</CardTitle>
            <CardDescription>
              {t.auth.loginDesc}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t.auth.email}</Label>
                <div className="relative">
                  <MailIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t.auth.emailPlaceholder}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t.auth.password}</Label>
                <div className="relative">
                  <LockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading}
                style={companyInfo?.primary_color ? { 
                  backgroundColor: primaryColor,
                  color: "white"
                } : {}}
              >
                {isLoading ? t.landing.signingIn : t.landing.signIn}
              </Button>

              <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                {t.landing.noAccount}{" "}
                <Button
                  type="button"
                  variant="link"
                  className="p-0 h-auto text-blue-600 hover:underline dark:text-blue-400"
                  onClick={() => navigate("/cadastro")}
                >
                  {t.landing.createAccount}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Landing;

